/**
 * Live boardgame.io client factory — the single runtime engine-import site
 * in apps/arena-client/.
 *
 * The sole runtime import of `@legendary-arena/game-engine` lives here so
 * that every other file in arena-client remains a `import type` consumer.
 * Grep verification in WP-090 Commit A verifies this invariant.
 *
 * The factory returns a narrow handle exposing exactly three methods:
 * `start()`, `stop()`, and `submitMove(name, ...args)`. The underlying
 * boardgame.io Client, its subscribe mechanism, and its moves bag are
 * intentionally not re-exported — every future gameplay UI packet must go
 * through `submitMove` for intent submission.
 */

import { LegendaryGame } from '@legendary-arena/game-engine';
import type { UIState } from '@legendary-arena/game-engine';
// why: boardgame.io v0.50 ships proxy-directory entries (`client/`,
// `multiplayer/`) whose `package.json` points to CJS via the `main` field
// and ESM via the (non-standard) `module` field. Under Vite this worked
// historically, but Node's native ESM resolver (and tsx by extension)
// rejects the directory import and fails to locate named bindings on the
// loose ESM artifact. Importing from the published CJS bundle via a
// namespace import (`import * as pkg`) sidesteps both CJS↔ESM interop
// quirks uniformly: tsx receives the CJS `module.exports` object under
// `pkg.default`; Vite receives the same object as `pkg` itself (its
// `__esModule: true` interop shim otherwise collapses the default lookup
// to undefined because this package sets `__esModule` but exports
// `Client` as a named property, not as `default`). Reading through the
// fallback chain `(pkg as any).Client ?? pkg.default?.Client` covers both.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — boardgame.io v0.50 ships no .d.ts alongside the dist CJS bundle.
import * as boardgameioClient from 'boardgame.io/dist/cjs/client.js';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — boardgame.io v0.50 ships no .d.ts alongside the dist CJS bundle.
import * as boardgameioMultiplayer from 'boardgame.io/dist/cjs/multiplayer.js';

type ClientCtor = (args: BgioClientFactoryArgs) => BgioClientLike;
type SocketIOCtor = (opts: { server: string }) => unknown;

// why: `pkg.Client` is the direct property set by the CJS bundle;
// `pkg.default.Client` is the path Vite's interop exposes when it rewrites
// the default import through `__esModule`. One of the two is always the
// callable constructor across tsx / Node / Vite runtimes.
const bgioClientNs = boardgameioClient as unknown as {
  Client?: ClientCtor;
  default?: { Client?: ClientCtor };
};
const bgioMultiplayerNs = boardgameioMultiplayer as unknown as {
  SocketIO?: SocketIOCtor;
  default?: { SocketIO?: SocketIOCtor };
};

const resolvedClient: ClientCtor | undefined =
  bgioClientNs.Client ?? bgioClientNs.default?.Client;
const resolvedSocketIO: SocketIOCtor | undefined =
  bgioMultiplayerNs.SocketIO ?? bgioMultiplayerNs.default?.SocketIO;

if (resolvedClient === undefined) {
  throw new Error(
    'boardgame.io Client constructor could not be resolved from the CJS bundle. ' +
      'Check that boardgame.io v0.50 is installed and that the dist path has not changed.',
  );
}
if (resolvedSocketIO === undefined) {
  throw new Error(
    'boardgame.io SocketIO transport could not be resolved from the CJS bundle. ' +
      'Check that boardgame.io v0.50 is installed and that the dist path has not changed.',
  );
}

const Client: ClientCtor = resolvedClient;
const SocketIO: SocketIOCtor = resolvedSocketIO;

import { useUiStateStore } from '../stores/uiState.js';
import { useConnectionStore } from '../stores/connection.js';
import { usePreplanStore } from '../stores/preplan.js';
import { detectPlayerAffectingMutations } from '../preplan/mutationDetector.js';
import { executeDisruptionPipeline } from '@legendary-arena/preplan';
import { applyDisruptionToStore } from '../preplan/preplanLifecycle.js';

// why: WP-312 / D-24097 — the move-acknowledgment watchdog deadline. After the
// player submits a move, the client's `_stateID` must advance (a server frame)
// within this window; if it does not, the move was dropped/rejected (the
// connected-desync wedge) and the client force-resyncs. Sized conservatively
// (4s) so a normal rapid-click race — which self-heals in well under a second
// when the real patch arrives — never trips it, while a genuine wedge (idle
// server, silently-rejected stale move) still recovers within a few seconds.
// This signal is reliable ONLY because every engine move is `client: false`
// (D-10008): with no client-side prediction, `_stateID` advances solely on a
// server-authoritative frame, so a non-advancing `_stateID` means the server
// did not acknowledge the move.
export const MOVE_ACK_TIMEOUT_MS = 4000;

// why: WP-312 / D-24097 — minimum gap between automatic resyncs. After the
// watchdog fires a resync, further auto-resyncs are suppressed for this window
// so a persistently-stuck client (or button-mashing during a real outage)
// cannot trigger a resync storm (each resync tears down and re-establishes the
// socket, which is disruptive). A manual `resync()` from the WP-311 banner is
// not gated by this cooldown.
export const RESYNC_COOLDOWN_MS = 8000;

// why: the spectator-staleness watchdog deadline. While it is NOT the viewer's
// turn (another seat — e.g. a bot ally — is acting), the client only advances on
// server-pushed frames; if none arrives within this window the client has
// silently fallen behind (a frame dropped, or a fast/transparent socket.io
// reconnect that never surfaced `isConnected:false`, so the WP-312 move-ack
// watchdog and the D-24232 reconnect-resync both miss it — the observed
// bot-ally freeze where the server advanced a turn but the client stayed put).
// A resync then re-anchors to authoritative state. Sized well above a healthy
// bot's per-move cadence (~1s) so a normally-progressing turn — which pushes a
// frame per move, resetting this timer — never trips it; only a genuine
// no-frames-for-15s gap while spectating does.
export const SPECTATOR_STALE_TIMEOUT_MS = 15000;

/**
 * The minimal subset of boardgame.io's Client object that this module uses.
 * Declared structurally so tests can inject a lightweight stand-in without
 * pulling in the real boardgame.io runtime.
 */
export interface BgioClientLike {
  start(): void;
  stop(): void;
  moves: Record<string, (...args: unknown[]) => void>;
  // why: boardgame.io delivers `ClientState<G> = null | (State<G> & {
  // isConnected: boolean })` to subscribers, so every frame carries the
  // transport status (`isConnected`, from `transport.isConnected`) and the
  // authoritative `_stateID` alongside the `G` projection. WP-090 read only
  // `G`; WP-311 also reads `isConnected` / `_stateID` to drive the reconnect
  // banner. The two fields are optional here so test stubs may omit them.
  subscribe(
    listener: (
      state:
        | { G?: unknown; isConnected?: boolean; _stateID?: number }
        | null
        | undefined,
    ) => void,
  ): unknown;
}

export interface BgioClientFactoryArgs {
  game: unknown;
  multiplayer: unknown;
  matchID: string;
  playerID: string;
  credentials: string;
}

export type BgioClientFactory = (args: BgioClientFactoryArgs) => BgioClientLike;

export interface CreateLiveClientOptions {
  matchID: string;
  playerID: string;
  credentials: string;
  serverUrl: string;
  // why: viewerPlayerId is needed by the mutation detector to distinguish
  // mutations affecting the viewer's pre-plan from irrelevant changes to
  // other players' state. Optional today because App.vue's caller site is
  // outside the WP-070 allowlist — the middleware gracefully skips when
  // the field is absent.
  viewerPlayerId?: string;
}

export interface LiveClientHandle {
  start(): void;
  stop(): void;
  submitMove(name: string, ...args: unknown[]): void;
  /**
   * Forces a re-sync with the server, re-anchoring the client's `_stateID`
   * to the server's authoritative state. Used to recover a client that has
   * fallen behind (the WP-311 desync freeze) without a full page reload.
   */
  resync(): void;
}

function defaultClientFactory(args: BgioClientFactoryArgs): BgioClientLike {
  return Client(args) as unknown as BgioClientLike;
}

let activeClientFactory: BgioClientFactory = defaultClientFactory;
let liveClientCallLog: CreateLiveClientOptions[] = [];

/**
 * Replaces the internal Client factory with a test double.
 * Pass `null` to restore the real boardgame.io factory.
 *
 * Testing-only. Production code must never call this.
 */
export function setClientFactoryForTesting(
  factory: BgioClientFactory | null,
): void {
  activeClientFactory = factory ?? defaultClientFactory;
}

/**
 * Returns the ordered list of options passed to `createLiveClient` since the
 * last call to {@link resetLiveClientCallLog}. Testing-only.
 */
export function getLiveClientCallLog(): readonly CreateLiveClientOptions[] {
  return liveClientCallLog;
}

/** Clears the recorded call log. Testing-only. */
export function resetLiveClientCallLog(): void {
  liveClientCallLog = [];
}

/**
 * Creates a live boardgame.io client wired to the running server.
 *
 * The returned handle subscribes to every server-pushed state frame and
 * writes the `G` slice (post WP-089 playerView projection) into the Pinia
 * UI store via {@link useUiStateStore.setSnapshot}. Downstream HUD
 * components read that store and re-render reactively.
 *
 * @param options  Match identity + credentials + server URL.
 * @returns Exactly `{ start, stop, submitMove }` — never the underlying client.
 */
export function createLiveClient(
  options: CreateLiveClientOptions,
): LiveClientHandle {
  liveClientCallLog.push(options);

  const client = activeClientFactory({
    game: LegendaryGame,
    multiplayer: SocketIO({ server: options.serverUrl }),
    matchID: options.matchID,
    playerID: options.playerID,
    credentials: options.credentials,
  });

  let previousUIState: UIState | null = null;

  // why: WP-312 / D-24097 — move-acknowledgment watchdog state. `latestStateId`
  // tracks the newest server-authoritative `_stateID` (updated in the subscribe
  // callback). `pendingBaselineStateId` is the `_stateID` captured when a move
  // was submitted; the move is "acknowledged" once `latestStateId` advances past
  // it. `resyncCoolingDown` suppresses auto-resync storms after a fire.
  let latestStateId: number | null = null;
  let pendingBaselineStateId: number | null = null;
  let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
  let resyncCoolingDown = false;
  let cooldownTimer: ReturnType<typeof setTimeout> | null = null;

  // why: reconnect-resync state — a transport drop-and-restore (the socket goes
  // down on a server restart, then reconnects) must force a fresh sync, because
  // boardgame.io does not reliably re-anchor the match on reconnect: the client
  // can come back "connected" yet stay pinned to a stale `_stateID` while the
  // server has advanced. That is invisible to both existing safety nets — the
  // WP-311 banner shows only while `isConnected` is false, and the WP-312
  // watchdog arms only after the viewer submits a move — so a client waiting on
  // ANOTHER seat (e.g. a bot ally's turn) after a restart freezes silently.
  // `previousIsConnected` holds the prior frame's status; `hasBeenConnected`
  // latches the first successful connect so the very first connect is never
  // mistaken for a reconnect; `reconnectResyncTimer` defers the tear-down out of
  // the subscribe frame.
  let previousIsConnected: boolean | null = null;
  let hasBeenConnected = false;
  let reconnectResyncTimer: ReturnType<typeof setTimeout> | null = null;

  // why: spectator-staleness watchdog + tab-focus resync state. The watchdog is
  // (re-)armed on every frame while it is another seat's turn and fires a resync
  // if no further frame arrives within SPECTATOR_STALE_TIMEOUT_MS.
  // `visibilityHandler` is retained so the document listener can be removed on
  // stop(). Both are additional recovery paths that do NOT depend on observing a
  // socket disconnect (the D-24232 gap).
  let spectatorWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
  let visibilityHandler: (() => void) | null = null;

  /**
   * Re-anchors the client to the server's authoritative state by tearing down
   * and re-establishing the boardgame.io client (`stop()` then `start()`),
   * which re-runs the SocketIO connect → server `onSync` handshake. Shared by
   * the handle's manual `resync()` (WP-311 banner), the watchdog fire, and the
   * reconnect-resync.
   */
  function performResync(): void {
    client.stop();
    client.start();
  }

  /**
   * Opens the auto-resync cooldown window so a flapping connection or a
   * button-mash during a real outage cannot trigger a resync storm (each resync
   * tears down and re-establishes the socket). Shared by the watchdog fire and
   * the reconnect-resync; a manual `resync()` from the WP-311 banner is NOT
   * gated by this cooldown.
   */
  function beginResyncCooldown(): void {
    resyncCoolingDown = true;
    if (cooldownTimer !== null) {
      clearTimeout(cooldownTimer);
    }
    cooldownTimer = setTimeout(() => {
      resyncCoolingDown = false;
      cooldownTimer = null;
    }, RESYNC_COOLDOWN_MS);
  }

  /** Clears the pending move watchdog (timer + baseline). */
  function clearWatchdog(): void {
    if (watchdogTimer !== null) {
      clearTimeout(watchdogTimer);
      watchdogTimer = null;
    }
    pendingBaselineStateId = null;
  }

  /**
   * Fired when a submitted move is not acknowledged within
   * {@link MOVE_ACK_TIMEOUT_MS}: the move was dropped/rejected (the wedge), so
   * force a resync and open the cooldown window so we do not auto-resync again
   * immediately.
   */
  function onWatchdogFire(): void {
    watchdogTimer = null;
    pendingBaselineStateId = null;
    performResync();
    beginResyncCooldown();
    // why: a client-diagnostic tally of an auto-recovery firing (WP-429 /
    // D-24250) — the count rides the connection store into the WP-428 transport
    // block; no recovery behavior changes. The move-ack watchdog is gated
    // upstream in armWatchdog (it no-ops while cooling down), so reaching here
    // always means an actual resync fired.
    useConnectionStore().recordMoveAckResync();
  }

  /**
   * Fired when the transport transitions from disconnected back to connected
   * after a genuine prior connection (a reconnect, not the first connect):
   * force a fresh sync so a client that reconnected to a restarted server but
   * stayed pinned to a stale `_stateID` catches up to the server's authoritative
   * state. Gated by the shared cooldown so the reconnect frame produced by the
   * resync's own `stop()`/`start()` does not re-trigger it into a loop. The
   * tear-down is deferred to a timer so the client is not stopped inside its own
   * subscribe frame.
   */
  function onTransportReconnect(): void {
    if (resyncCoolingDown) {
      return;
    }
    beginResyncCooldown();
    // why: a client-diagnostic tally of an auto-recovery firing (WP-429 /
    // D-24250) — the count rides the connection store into the WP-428 transport
    // block; no recovery behavior changes. Past the inline resyncCoolingDown
    // gate, so a cooldown-suppressed reconnect frame (the resync's own
    // stop()/start()) does not increment.
    useConnectionStore().recordReconnectResync();
    reconnectResyncTimer = setTimeout(() => {
      reconnectResyncTimer = null;
      // why: clear any armed move-watchdog before tearing down — the resync
      // re-anchors state now, so a watchdog fire against the stopped client
      // would be a redundant second resync.
      clearWatchdog();
      performResync();
    }, 0);
  }

  /**
   * Arms the watchdog after a real move is dispatched. No-ops when cooling down
   * or before the first server frame (no baseline `_stateID` to compare
   * against). Re-arming resets the deadline so a burst of moves is covered by a
   * single pending window.
   */
  function armWatchdog(): void {
    if (resyncCoolingDown || latestStateId === null) {
      return;
    }
    pendingBaselineStateId = latestStateId;
    if (watchdogTimer !== null) {
      clearTimeout(watchdogTimer);
    }
    watchdogTimer = setTimeout(onWatchdogFire, MOVE_ACK_TIMEOUT_MS);
  }

  /** Clears the spectator-staleness watchdog timer. */
  function clearSpectatorWatchdog(): void {
    if (spectatorWatchdogTimer !== null) {
      clearTimeout(spectatorWatchdogTimer);
      spectatorWatchdogTimer = null;
    }
  }

  /**
   * Forces a resync, gated by the shared cooldown and clearing the move-ack
   * watchdog (we are re-anchoring now). Used by the tab-focus handler (a
   * one-shot, event-driven recovery). The spectator-staleness watchdog uses the
   * self-re-arming {@link onSpectatorWatchdogFire} instead, so this helper serves
   * the tab-focus path alone — its increment routes to `recordTabFocusResync`
   * without needing a cause discriminator.
   */
  function forceCooldownGatedResync(): void {
    if (resyncCoolingDown) {
      return;
    }
    clearWatchdog();
    performResync();
    beginResyncCooldown();
    // why: a client-diagnostic tally of an auto-recovery firing (WP-429 /
    // D-24250) — the count rides the connection store into the WP-428 transport
    // block; no recovery behavior changes. Past the resyncCoolingDown gate, so a
    // cooldown-suppressed tab-focus event does not increment.
    useConnectionStore().recordTabFocusResync();
  }

  /**
   * The spectator-staleness watchdog fire: attempt a cooldown-gated resync, then
   * RE-ARM so recovery keeps being retried until a frame lands or the client
   * stops. Without the re-arm a single fire that is swallowed by an open resync
   * cooldown (some other resync fired within the preceding RESYNC_COOLDOWN_MS)
   * is never retried — nothing re-arms the watchdog because the whole failure
   * mode is that NO further frame arrives. That is the terminal-frame freeze: a
   * non-acting seat that silently missed the gameover broadcast would sit on the
   * pre-gameover board forever. Re-arming turns "one missed recovery = permanent
   * freeze" into "retry every window until the end screen (or any frame) lands."
   *
   * The re-arm is harmless while frames flow: the next frame's
   * {@link updateSpectatorWatchdog} clears and re-arms this timer (so never a
   * duplicate), and the recovering gameover frame clears it outright (that frame
   * is not "spectating another seat", so the watchdog is not re-armed). `stop()`
   * clears it too.
   */
  function onSpectatorWatchdogFire(): void {
    if (!resyncCoolingDown) {
      clearWatchdog();
      performResync();
      beginResyncCooldown();
      // why: a client-diagnostic tally of an auto-recovery firing (WP-429 /
      // D-24250) — the count rides the connection store into the WP-428
      // transport block; no recovery behavior changes. Inside the
      // !resyncCoolingDown branch, so a fire swallowed by an open cooldown (the
      // re-arm-and-retry case below) does not increment — only an actual resync.
      useConnectionStore().recordSpectatorStaleResync();
    }
    spectatorWatchdogTimer = setTimeout(
      onSpectatorWatchdogFire,
      SPECTATOR_STALE_TIMEOUT_MS,
    );
  }

  /**
   * (Re-)arms the spectator-staleness watchdog from the latest frame. Armed only
   * while the client is connected, the game is not over, and it is ANOTHER seat's
   * turn (the viewer's own seat is `options.playerID`; its turn is covered by the
   * WP-312 move-ack watchdog instead). Every frame resets the deadline, so a
   * normally-progressing turn never fires it; a 15s gap with no frame does.
   *
   * @param currentUIState - The frame's audience-filtered UIState (or null).
   * @param isConnected - The frame's transport status.
   */
  function updateSpectatorWatchdog(
    currentUIState: UIState | null,
    isConnected: boolean,
  ): void {
    const activePlayerId = currentUIState?.game?.activePlayerId;
    const isSpectatingOtherSeat =
      isConnected === true &&
      currentUIState !== null &&
      !currentUIState.gameOver &&
      typeof activePlayerId === 'string' &&
      activePlayerId !== options.playerID;
    if (!isSpectatingOtherSeat) {
      clearSpectatorWatchdog();
      return;
    }
    clearSpectatorWatchdog();
    spectatorWatchdogTimer = setTimeout(
      onSpectatorWatchdogFire,
      SPECTATOR_STALE_TIMEOUT_MS,
    );
  }

  /**
   * Resyncs when the tab returns to the foreground. A backgrounded tab both
   * starves the socket (missed frames) AND throttles the spectator watchdog's own
   * timer, so foregrounding is the reliable moment to re-anchor. Cooldown-gated.
   */
  function onVisibilityChange(): void {
    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible'
    ) {
      forceCooldownGatedResync();
    }
  }

  client.subscribe((state) => {
    // why: WP-311 — record the transport connection status on EVERY frame,
    // before any G handling, so the reconnect banner reflects a drop even on
    // frames whose G projection is null/malformed (the early returns below).
    // boardgame.io sets `state.isConnected` from `transport.isConnected`; a
    // null frame means no connection, so `isConnected` is treated as false.
    const isConnected = state?.isConnected === true;
    // why: read the frame's stateID for the connection store; never assign
    // `_stateID` (the client never pokes boardgame.io's authoritative version —
    // resync() re-anchors it via the framework's own sync instead).
    const rawStateId = state?._stateID;
    const stateId = typeof rawStateId === 'number' ? rawStateId : null;
    useConnectionStore().setConnected(isConnected, stateId);

    // why: reconnect-resync — fire a fresh sync only on a genuine reconnect
    // (previous frame disconnected, this frame connected, AND the client had
    // connected at least once before). Capturing `hasBeenConnected` BEFORE it is
    // latched below ensures the FIRST connect (previousIsConnected null→true, or
    // a pre-connect false frame → true) is never treated as a reconnect.
    const wasConnectedBefore = hasBeenConnected;
    if (isConnected === true) {
      hasBeenConnected = true;
    }
    if (
      isConnected === true &&
      previousIsConnected === false &&
      wasConnectedBefore === true
    ) {
      onTransportReconnect();
    }
    previousIsConnected = isConnected;

    // why: WP-312 / D-24097 — track the newest server-authoritative `_stateID`
    // and, if it has advanced past a pending move's baseline, acknowledge that
    // move (clear the watchdog). Runs before any G handling so an ack registers
    // even on a frame whose G projection is null/malformed.
    if (stateId !== null) {
      latestStateId = stateId;
      if (
        pendingBaselineStateId !== null &&
        latestStateId > pendingBaselineStateId
      ) {
        clearWatchdog();
      }
    }

    const projection = state?.G;
    if (projection !== null && projection !== undefined && typeof projection !== 'object') {
      // why: fail-closed on malformed server frames — a non-object projection
      // cannot be cast safely to UIState. Log once via console.warn (dev
      // surface only) and coalesce to null so the HUD drops to the empty
      // state rather than rendering garbage. FIX-22 per copilot review.
      console.warn(
        '[wp-090] subscribe received non-object state.G; coalescing to null',
      );
      useUiStateStore().setSnapshot(null);
      return;
    }
    // why: WP-089's playerView reshapes the client-visible G to the exact
    // UIState contract consumed by the HUD. The server enforces that
    // reshape; the client only trusts the projection.
    const currentUIState = (projection ?? null) as UIState | null;
    useUiStateStore().setSnapshot(currentUIState);

    // why: (re-)arm the spectator-staleness watchdog from this frame — while it
    // is another seat's turn, a resync fires if no further frame arrives within
    // the window, recovering a silent client desync that never surfaced a socket
    // disconnect (the D-24232 reconnect trigger cannot catch that case).
    updateSpectatorWatchdog(currentUIState, isConnected);

    // why: middleware runs after the UIState store write so that components
    // see the causal state change before the disruption notification. The
    // store must reflect the new state before disruption processing.
    if (currentUIState === null) {
      return;
    }

    if (previousUIState === null) {
      previousUIState = currentUIState;
      return;
    }

    if (previousUIState === currentUIState) {
      return;
    }

    if (options.viewerPlayerId === undefined) {
      previousUIState = currentUIState;
      return;
    }

    const preplanStore = usePreplanStore();
    if (!preplanStore.isActive) {
      previousUIState = currentUIState;
      return;
    }

    const mutations = detectPlayerAffectingMutations(
      previousUIState,
      currentUIState,
      options.viewerPlayerId,
    );

    for (const mutation of mutations) {
      const result = executeDisruptionPipeline(preplanStore.current!, mutation);
      if (result !== null && result.requiresImmediateNotification === true) {
        applyDisruptionToStore({ store: preplanStore, result });
        break;
      }
    }

    previousUIState = currentUIState;
  });

  // why: register the tab-focus resync (see onVisibilityChange). Guarded for
  // non-DOM (SSR/test) environments; the handler ref is retained so stop() can
  // remove it (no leaked listener across a match teardown).
  if (
    typeof document !== 'undefined' &&
    typeof document.addEventListener === 'function'
  ) {
    visibilityHandler = onVisibilityChange;
    document.addEventListener('visibilitychange', visibilityHandler);
  }

  return {
    start: () => client.start(),
    // why: WP-312 — clear the watchdog + cooldown timers before stopping so no
    // timer outlives the client (mirrors the WP-262 interval-cleanup discipline;
    // a leaked timer would keep firing against a stopped client).
    stop: (): void => {
      clearWatchdog();
      if (cooldownTimer !== null) {
        clearTimeout(cooldownTimer);
        cooldownTimer = null;
      }
      // why: clear a pending reconnect-resync timer so it never fires against a
      // client the caller has torn down (mirrors the watchdog/cooldown cleanup).
      if (reconnectResyncTimer !== null) {
        clearTimeout(reconnectResyncTimer);
        reconnectResyncTimer = null;
      }
      // why: clear the spectator-staleness watchdog + remove the tab-focus
      // listener so neither fires a resync against a torn-down client.
      clearSpectatorWatchdog();
      if (
        visibilityHandler !== null &&
        typeof document !== 'undefined' &&
        typeof document.removeEventListener === 'function'
      ) {
        document.removeEventListener('visibilitychange', visibilityHandler);
        visibilityHandler = null;
      }
      client.stop();
    },
    // why: the client submits intent via boardgame.io's move API; the
    // server receives the intent and dispatches to the authoritative engine.
    // The client never computes outcomes.
    // why: WP-312 — arm the move-acknowledgment watchdog only after a real move
    // was dispatched (never for an unknown/no-op name), so a dropped/rejected
    // move that never advances `_stateID` triggers an auto-resync.
    submitMove: (name: string, ...args: unknown[]): void => {
      const move = client.moves[name];
      if (typeof move === 'function') {
        move(...args);
        armWatchdog();
      }
    },
    // why: WP-311 / D-24096 — force a re-sync by tearing down and
    // re-establishing the boardgame.io client (`stop()` then `start()`). That
    // re-runs the SocketIO connect → server `onSync` handshake, which re-anchors
    // the client's `_stateID` to the server's authoritative state. This is the
    // recovery for a client wedged one `_stateID` behind (the freeze this WP
    // fixes) — it re-reads authoritative state via boardgame.io's own sync,
    // never pokes `_stateID`, fabricates a frame, or calls a server endpoint.
    // why: WP-312 — a manual resync also clears any pending watchdog (we are
    // re-anchoring now); it is NOT gated by the auto-resync cooldown.
    resync: (): void => {
      clearWatchdog();
      performResync();
    },
  };
}
